import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { X, Send, Phone, Loader2, CheckCircle2 } from "lucide-react";
import { COMPANY, SERVICES } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_NAMES = SERVICES.map((s) => s.title).concat(["Plumbing — other", "Not sure yet"]);
const PROPERTY = ["House", "Flat / Apartment", "Bungalow", "Commercial"];
const URGENCY = ["Emergency — today", "This week", "Planning ahead", "Just a quote"];

const field =
  "w-full rounded-xl border border-white/10 bg-[#0a0a0c] px-4 py-3 text-white placeholder:text-zinc-600 focus:border-[#f5641e] focus:outline-none transition-colors";

export const QuoteForm = ({ open, onClose }) => {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", postcode: "",
    service: SERVICE_NAMES[0], propertyType: PROPERTY[0], urgency: URGENCY[1], message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, email: form.email.trim() || undefined };
      await axios.post(`${API}/quote`, payload);
      setDone(true);
      confetti({ particleCount: 120, spread: 75, origin: { y: 0.35 }, colors: ["#f5641e", "#ffb800", "#ffffff"] });
      toast.success("Quote request sent to SX Heating!");
    } catch (err) {
      toast.error("Something went wrong — please call us on " + COMPANY.phone);
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => {
      setDone(false);
      setForm({ name: "", phone: "", email: "", postcode: "", service: SERVICE_NAMES[0], propertyType: PROPERTY[0], urgency: URGENCY[1], message: "" });
    }, 300);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={close} />
          <motion.div
            data-testid="quote-form-section"
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-3xl border border-white/10 bg-[#121217] p-6 sm:p-8"
          >
            <button onClick={close} data-testid="quote-close-btn" className="absolute right-5 top-5 text-zinc-500 hover:text-white">
              <X className="h-5 w-5" />
            </button>

            {done ? (
              <div className="py-10 text-center" data-testid="quote-success">
                <CheckCircle2 className="mx-auto h-16 w-16 text-green-400" />
                <h3 className="mt-5 font-display text-2xl font-bold">Request received!</h3>
                <p className="mt-3 text-zinc-400">
                  Thanks {form.name.split(" ")[0]} — your enquiry has been emailed to our team.
                  We'll be in touch shortly. Need us now?
                </p>
                <a href={`tel:${COMPANY.phoneIntl}`} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#f5641e] px-6 py-3 font-bold text-[#0a0a0c]">
                  <Phone className="h-4 w-4" /> Call {COMPANY.phone}
                </a>
              </div>
            ) : (
              <>
                <span className="font-mono-sx text-xs uppercase tracking-[0.25em] text-[#f5641e]">Free · no obligation</span>
                <h3 className="mt-2 font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight">Request a quote</h3>
                <p className="mt-2 text-sm text-zinc-400">We'll email your details to our Essex team and get straight back to you.</p>

                <form onSubmit={submit} className="mt-6 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <input data-testid="quote-name" className={field} placeholder="Full name*" value={form.name} onChange={set("name")} />
                    <input data-testid="quote-phone" className={field} placeholder="Phone*" value={form.phone} onChange={set("phone")} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input data-testid="quote-email" className={field} placeholder="Email" type="email" value={form.email} onChange={set("email")} />
                    <input data-testid="quote-postcode" className={field} placeholder="Postcode / town" value={form.postcode} onChange={set("postcode")} />
                  </div>
                  <select data-testid="quote-service" className={field} value={form.service} onChange={set("service")}>
                    {SERVICE_NAMES.map((s) => <option key={s} className="bg-[#0a0a0c]">{s}</option>)}
                  </select>
                  <div className="grid grid-cols-2 gap-3">
                    <select data-testid="quote-property" className={field} value={form.propertyType} onChange={set("propertyType")}>
                      {PROPERTY.map((s) => <option key={s} className="bg-[#0a0a0c]">{s}</option>)}
                    </select>
                    <select data-testid="quote-urgency" className={field} value={form.urgency} onChange={set("urgency")}>
                      {URGENCY.map((s) => <option key={s} className="bg-[#0a0a0c]">{s}</option>)}
                    </select>
                  </div>
                  <textarea data-testid="quote-message" rows={3} className={field} placeholder="Tell us about the job (optional)" value={form.message} onChange={set("message")} />

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="quote-submit-btn"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#f5641e] px-6 py-4 font-bold text-[#0a0a0c] hover:bg-[#ff7a38] transition-colors disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />}
                    {loading ? "Sending..." : "Send my request"}
                  </button>
                  <p className="text-center text-xs text-zinc-600">Or call us direct on {COMPANY.phone}</p>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
